import type { BrowserApiAudioNote, BrowserAudioNote, BrowserAudioNoteState } from './browser-types'
import { emitBrowserMainStoreSet, ensureStoreSnapshotNamespace } from './browser-store'
import { fetchBrowserApi } from './browser-http'
import { generateSecureId, parseDateValue } from './browser-utils'

const BROWSER_AUDIO_NOTES_STORAGE_KEY = 'highlight:browser-audio-notes'
const BROWSER_AUDIO_NOTE_STATE_KEY = 'highlight:browser-audio-note-state'
const BROWSER_AUDIO_NOTE_TRANSCRIPT_KEY = 'highlight:browser-audio-note-transcript-entries'

// --- Helpers ---

const isHostSpeaker = (speaker: string): boolean => {
  const normalizedSpeaker = speaker.toLowerCase()
  return normalizedSpeaker === 'self' || normalizedSpeaker === 'me' || normalizedSpeaker.includes('host')
}

const mapTranscriptStringToEntries = (transcript: string, startedAt: Date): Array<unknown> => {
  if (typeof transcript !== 'string' || !transcript.trim()) {
    return []
  }

  const parsedMessages = transcript
    .split('\n')
    .map((line) => line.trim())
    .map((line) => {
      const match = line.match(/^(\d{1,2}:\d{2}:\d{2}\s+(?:AM|PM))\s+-\s+(.+?)(?:\s*:\s*|\s+-\s+)(.*)$/i)
      if (!match) {
        return null
      }
      const [, timeString, speaker, text] = match
      const timeMatch = timeString.match(/(\d{1,2}):(\d{1,2}):(\d{1,2})\s*(AM|PM)/i)
      if (!timeMatch) {
        return null
      }
      let hours = Number.parseInt(timeMatch[1], 10)
      const minutes = Number.parseInt(timeMatch[2], 10)
      const seconds = Number.parseInt(timeMatch[3], 10)
      const period = timeMatch[4].toLowerCase()
      if (period === 'pm' && hours < 12) {
        hours += 12
      } else if (period === 'am' && hours === 12) {
        hours = 0
      }
      const absoluteDate = new Date(startedAt)
      absoluteDate.setHours(hours, minutes, seconds, 0)
      return {
        speaker: speaker.trim(),
        text: text.trim() || '.',
        absoluteTime: absoluteDate.toISOString(),
      }
    })
    .filter((message): message is { speaker: string; text: string; absoluteTime: string } => message !== null)

  if (parsedMessages.length === 0) {
    return []
  }

  return parsedMessages.map((message) => {
    const absoluteMs = new Date(message.absoluteTime).getTime()
    const relativeMs = absoluteMs - startedAt.getTime()
    return {
      speaker: message.speaker,
      text: message.text,
      isHost: isHostSpeaker(message.speaker),
      timestamp: {
        absolute: message.absoluteTime,
        relative: relativeMs,
      },
    }
  })
}

const mapApiAudioNoteToBrowserAudioNote = (note: BrowserApiAudioNote): BrowserAudioNote => {
  const startedAt = parseDateValue(note.startedAt)
  const transcriptEntries = Array.isArray(note.transcriptEntries)
    ? note.transcriptEntries
    : typeof note.transcript === 'string'
      ? mapTranscriptStringToEntries(note.transcript, startedAt)
      : []

  return {
    id: note.id,
    userId: typeof note.userId === 'string' ? note.userId : '',
    startedAt,
    endedAt: parseDateValue(note.endedAt),
    title: typeof note.title === 'string' ? note.title : 'Meeting Note',
    summary: typeof note.summary === 'string' ? note.summary : '',
    meeting: note.meeting,
    shareLink: typeof note.shareLink === 'string' ? note.shareLink : undefined,
    isActive: typeof note.isActive === 'boolean' ? note.isActive : undefined,
    transcriptEntries,
    sharedRecipients: [],
  }
}

const parseSavedNoteConfirmation = (raw: unknown): BrowserAudioNoteState['savedNoteConfirmation'] => {
  if (!raw || typeof raw !== 'object') {
    return null
  }
  const { noteId, savedAt } = raw as Record<string, unknown>
  return typeof noteId === 'string' && typeof savedAt === 'number' ? { noteId, savedAt } : null
}

// --- Default state ---

export const buildDefaultAudioNoteState = (): BrowserAudioNoteState => ({
  status: 'idle',
  startTime: null,
  meeting: null,
  detectedMeeting: null,
  audioSessionId: null,
  activeNoteId: null,
  activeNoteTitle: null,
  savedNote: null,
  summaryNoteId: null,
  summaryPending: false,
  streamingSummary: '',
  summaryError: null,
  savedNoteConfirmation: null,
})

// --- Storage method ---

const getStorageMethod = (): 'local' | 'cloud' => {
  try {
    const settingsStore = ensureStoreSnapshotNamespace('settings.store')
    const storageMethod = settingsStore['audio-notes.storage-method']
    if (storageMethod === 'local' || storageMethod === 'cloud') {
      return storageMethod as 'local' | 'cloud'
    }
  } catch (error) {
    console.warn('[browser-runtime] Failed reading audio note storage method', error)
  }
  return 'cloud'
}

// --- LocalStorage persistence ---

export const readBrowserAudioNotes = (): Array<BrowserAudioNote> => {
  try {
    const rawNotes = localStorage.getItem(BROWSER_AUDIO_NOTES_STORAGE_KEY)
    if (!rawNotes) {
      return []
    }
    const parsedNotes = JSON.parse(rawNotes)
    if (!Array.isArray(parsedNotes)) {
      return []
    }
    return parsedNotes
      .filter(
        (note: unknown) => note && typeof note === 'object' && typeof (note as Record<string, unknown>).id === 'string',
      )
      .map((note: unknown) => {
        const n = note as Record<string, unknown>
        return {
          id: n.id as string,
          userId: typeof n.userId === 'string' ? n.userId : '',
          startedAt: parseDateValue(n.startedAt),
          endedAt: parseDateValue(n.endedAt),
          title: typeof n.title === 'string' ? n.title : 'Meeting Note',
          summary: typeof n.summary === 'string' ? n.summary : '',
          meeting: n.meeting,
          shareLink: typeof n.shareLink === 'string' ? n.shareLink : undefined,
          isActive: typeof n.isActive === 'boolean' ? n.isActive : undefined,
          transcriptEntries: Array.isArray(n.transcriptEntries) ? n.transcriptEntries : [],
          sharedRecipients: Array.isArray(n.sharedRecipients) ? n.sharedRecipients : [],
        } as BrowserAudioNote
      })
      .sort((a: BrowserAudioNote, b: BrowserAudioNote) => b.startedAt.getTime() - a.startedAt.getTime())
  } catch (error) {
    console.error('[browser-runtime] Failed to read audio notes from localStorage', error)
    return []
  }
}

const persistBrowserAudioNotes = (notes: Array<BrowserAudioNote>): void => {
  try {
    localStorage.setItem(
      BROWSER_AUDIO_NOTES_STORAGE_KEY,
      JSON.stringify(
        notes.map((note) => ({
          ...note,
          startedAt: note.startedAt.toISOString(),
          endedAt: note.endedAt.toISOString(),
        })),
      ),
    )
  } catch (error) {
    console.error('[browser-runtime] Failed to persist audio notes', error)
  }
}

// --- State persistence ---

export const readBrowserAudioNoteState = (): BrowserAudioNoteState => {
  try {
    const rawState = localStorage.getItem(BROWSER_AUDIO_NOTE_STATE_KEY)
    if (!rawState) {
      return buildDefaultAudioNoteState()
    }
    const parsedState = JSON.parse(rawState) as Record<string, unknown>
    const savedNoteRaw = parsedState.savedNote as Record<string, unknown> | null | undefined
    const savedNote = savedNoteRaw
      ? ({
          id: typeof savedNoteRaw.id === 'string' ? savedNoteRaw.id : generateSecureId(),
          userId: typeof savedNoteRaw.userId === 'string' ? savedNoteRaw.userId : '',
          startedAt: parseDateValue(savedNoteRaw.startedAt),
          endedAt: parseDateValue(savedNoteRaw.endedAt),
          title: typeof savedNoteRaw.title === 'string' ? savedNoteRaw.title : 'Meeting Note',
          summary: typeof savedNoteRaw.summary === 'string' ? savedNoteRaw.summary : '',
          meeting: savedNoteRaw.meeting,
          shareLink: typeof savedNoteRaw.shareLink === 'string' ? savedNoteRaw.shareLink : undefined,
          isActive: typeof savedNoteRaw.isActive === 'boolean' ? savedNoteRaw.isActive : undefined,
          transcriptEntries: Array.isArray(savedNoteRaw.transcriptEntries) ? savedNoteRaw.transcriptEntries : [],
          sharedRecipients: Array.isArray(savedNoteRaw.sharedRecipients) ? savedNoteRaw.sharedRecipients : [],
        } as BrowserAudioNote)
      : null
    return {
      status:
        typeof parsedState.status === 'string'
          ? (parsedState.status as BrowserAudioNoteState['status'])
          : buildDefaultAudioNoteState().status,
      startTime: parsedState.startTime ? parseDateValue(parsedState.startTime) : null,
      meeting: parsedState.meeting ?? null,
      detectedMeeting: parsedState.detectedMeeting ?? null,
      audioSessionId: typeof parsedState.audioSessionId === 'string' ? parsedState.audioSessionId : null,
      activeNoteId: typeof parsedState.activeNoteId === 'string' ? parsedState.activeNoteId : null,
      activeNoteTitle: typeof parsedState.activeNoteTitle === 'string' ? parsedState.activeNoteTitle : null,
      savedNote,
      summaryNoteId: typeof parsedState.summaryNoteId === 'string' ? parsedState.summaryNoteId : null,
      summaryPending: parsedState.summaryPending === true,
      streamingSummary: typeof parsedState.streamingSummary === 'string' ? parsedState.streamingSummary : '',
      summaryError: typeof parsedState.summaryError === 'string' ? parsedState.summaryError : null,
      savedNoteConfirmation: parseSavedNoteConfirmation(parsedState.savedNoteConfirmation),
    }
  } catch (error) {
    console.error('[browser-runtime] Failed to read audio note state', error)
    return buildDefaultAudioNoteState()
  }
}

const persistBrowserAudioNoteState = (state: BrowserAudioNoteState): void => {
  try {
    localStorage.setItem(
      BROWSER_AUDIO_NOTE_STATE_KEY,
      JSON.stringify({
        ...state,
        startTime: state.startTime ? state.startTime.toISOString() : null,
        savedNote: state.savedNote
          ? {
              ...state.savedNote,
              startedAt: state.savedNote.startedAt.toISOString(),
              endedAt: state.savedNote.endedAt.toISOString(),
            }
          : null,
      }),
    )
  } catch (error) {
    console.error('[browser-runtime] Failed to persist audio note state', error)
  }
}

export const readBrowserTranscriptEntries = (): Array<unknown> => {
  try {
    const rawEntries = localStorage.getItem(BROWSER_AUDIO_NOTE_TRANSCRIPT_KEY)
    if (!rawEntries) {
      return []
    }
    const parsedEntries = JSON.parse(rawEntries)
    return Array.isArray(parsedEntries) ? parsedEntries : []
  } catch (error) {
    console.error('[browser-runtime] Failed to read transcript entries', error)
    return []
  }
}

const persistBrowserTranscriptEntries = (entries: Array<unknown>): void => {
  try {
    localStorage.setItem(BROWSER_AUDIO_NOTE_TRANSCRIPT_KEY, JSON.stringify(entries))
  } catch (error) {
    console.error('[browser-runtime] Failed to persist transcript entries', error)
  }
}

// --- Event emission ---

const emitAudioNoteState = (state: BrowserAudioNoteState): void => {
  persistBrowserAudioNoteState(state)
  emitBrowserMainStoreSet('global.state', 'audio-note-state', state)
}

const emitTranscriptEntries = (entries: Array<unknown>): void => {
  persistBrowserTranscriptEntries(entries)
  emitBrowserMainStoreSet('global.state', 'audio-note-transcript-entries', entries)
}

// --- Cloud API operations ---

const fetchAudioNoteById = async (audioNoteId: string): Promise<BrowserAudioNote | undefined> => {
  try {
    const response = await fetchBrowserApi(`/api/v1/audio-notes/${audioNoteId}`)
    if (!response?.ok) {
      return undefined
    }
    const payload = (await response.json()) as { data?: BrowserApiAudioNote }
    if (!payload?.data) {
      return undefined
    }
    return mapApiAudioNoteToBrowserAudioNote(payload.data)
  } catch (error) {
    console.error('[browser-runtime] Failed to fetch audio note by ID', audioNoteId, error)
    return undefined
  }
}

const fetchAudioNotes = async (): Promise<Array<BrowserAudioNote>> => {
  try {
    const response = await fetchBrowserApi('/api/v1/audio-notes?limit=100')
    if (!response?.ok) {
      return []
    }
    const payload = (await response.json()) as { data?: Array<BrowserApiAudioNote> }
    const notes = Array.isArray(payload?.data) ? payload.data.map(mapApiAudioNoteToBrowserAudioNote) : []
    return notes.filter((note) => !note.isActive).sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime())
  } catch (error) {
    console.error('[browser-runtime] Failed to fetch audio notes list', error)
    return []
  }
}

const updateAudioNoteInCloud = async (
  noteId: string,
  updates: Record<string, unknown>,
): Promise<BrowserAudioNote | undefined> => {
  try {
    const response = await fetchBrowserApi(`/api/v1/audio-notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
    if (!response?.ok) {
      return undefined
    }
    const payload = (await response.json()) as { data?: BrowserApiAudioNote }
    if (!payload?.data) {
      return undefined
    }
    return mapApiAudioNoteToBrowserAudioNote(payload.data)
  } catch (error) {
    console.error('[browser-runtime] Failed to update audio note in cloud', noteId, error)
    return undefined
  }
}

const deleteAudioNoteInCloud = async (noteId: string): Promise<void> => {
  try {
    await fetchBrowserApi(`/api/v1/audio-notes/${noteId}`, {
      method: 'DELETE',
    })
  } catch (error) {
    console.error('[browser-runtime] Failed to delete audio note in cloud', noteId, error)
  }
}

// --- Router action handlers (exported for browser-router) ---

export const handleGetAudioNotes = (): Promise<Array<BrowserAudioNote>> | Array<BrowserAudioNote> => {
  if (getStorageMethod() === 'cloud') {
    return fetchAudioNotes()
  }
  return readBrowserAudioNotes()
}

export const handleGetAudioNotesWithinTimeWindow = (
  payload: unknown,
): Promise<Array<BrowserAudioNote>> | Array<BrowserAudioNote> => {
  const timeWindowDays = typeof payload === 'number' ? payload : 0
  const oldestTimestamp = Date.now() - Math.max(timeWindowDays, 0) * 24 * 60 * 60 * 1000
  if (getStorageMethod() === 'cloud') {
    return (async () => {
      const notes = await fetchAudioNotes()
      return notes.filter((note) => note.endedAt.getTime() >= oldestTimestamp)
    })()
  }
  return readBrowserAudioNotes().filter((note) => note.endedAt.getTime() >= oldestTimestamp)
}

export const handleGetAudioNote = (
  payload: unknown,
): Promise<BrowserAudioNote | undefined> | BrowserAudioNote | undefined => {
  const audioNoteId = typeof payload === 'string' ? payload : ''
  if (!audioNoteId) {
    return undefined
  }
  const localNote = readBrowserAudioNotes().find((note) => note.id === audioNoteId)
  if (localNote) {
    return localNote
  }
  if (getStorageMethod() === 'cloud') {
    return fetchAudioNoteById(audioNoteId)
  }
  return undefined
}

export const handleUpdateAudioNote = (payload: unknown): Promise<BrowserAudioNote | undefined> | undefined => {
  const updatePayload = payload as { id?: string; updates?: Record<string, unknown> } | undefined
  const noteId = updatePayload?.id
  if (!noteId) {
    return undefined
  }
  const updates = updatePayload?.updates ?? {}
  if (getStorageMethod() === 'cloud') {
    return (async () => {
      const updatedCloudNote = await updateAudioNoteInCloud(noteId, updates)
      if (updatedCloudNote) {
        const state = readBrowserAudioNoteState()
        if (state.savedNote?.id === noteId) {
          emitAudioNoteState({ ...state, savedNote: updatedCloudNote })
        }
      }
      return updatedCloudNote
    })()
  }
  const notes = readBrowserAudioNotes()
  const noteIndex = notes.findIndex((note) => note.id === noteId)
  if (noteIndex < 0) {
    return undefined
  }
  const existingNote = notes[noteIndex]
  const updatedNote: BrowserAudioNote = {
    ...existingNote,
    ...updates,
    startedAt: updates.startedAt ? parseDateValue(updates.startedAt) : existingNote.startedAt,
    endedAt: updates.endedAt ? parseDateValue(updates.endedAt) : existingNote.endedAt,
  }
  notes[noteIndex] = updatedNote
  persistBrowserAudioNotes(notes)

  const state = readBrowserAudioNoteState()
  if (state.savedNote?.id === noteId) {
    emitAudioNoteState({ ...state, savedNote: updatedNote })
  }
  return undefined
}

export const handleDeleteAudioNote = (payload: unknown): Promise<undefined> | undefined => {
  const noteId = typeof payload === 'string' ? payload : ''
  if (!noteId) {
    return undefined
  }

  const resetState = (): void => {
    const state = readBrowserAudioNoteState()
    if (state.savedNote?.id === noteId) {
      emitAudioNoteState({
        ...state,
        status: 'idle',
        savedNote: null,
        activeNoteId: null,
        activeNoteTitle: null,
        startTime: null,
        summaryNoteId: null,
        summaryPending: false,
        summaryError: null,
        streamingSummary: '',
        savedNoteConfirmation: null,
      })
    }
  }

  if (getStorageMethod() === 'cloud') {
    return (async () => {
      await deleteAudioNoteInCloud(noteId)
      resetState()
      return undefined
    })()
  }
  const notes = readBrowserAudioNotes().filter((note) => note.id !== noteId)
  persistBrowserAudioNotes(notes)
  resetState()
  return undefined
}

export const handleStartRecording = (payload: unknown): undefined => {
  const meeting = payload ?? null
  const startTime = new Date()
  const activeNoteId = generateSecureId()
  const state: BrowserAudioNoteState = {
    ...buildDefaultAudioNoteState(),
    status: 'recording',
    startTime,
    meeting,
    activeNoteId,
    activeNoteTitle: `Meeting Notes from ${startTime.toLocaleString()}`,
  }
  emitTranscriptEntries([])
  emitAudioNoteState(state)
  return undefined
}

export const handleStopRecording = (payload: unknown): undefined => {
  const shouldSave = payload === true
  const state = readBrowserAudioNoteState()
  if (state.status === 'idle' && !state.savedNote) {
    return undefined
  }

  if (!shouldSave) {
    emitTranscriptEntries([])
    emitAudioNoteState(buildDefaultAudioNoteState())
    return undefined
  }

  const startedAt = state.startTime ?? new Date()
  const endedAt = new Date()
  const transcriptEntries = readBrowserTranscriptEntries()
  const savedNote: BrowserAudioNote = {
    id: state.activeNoteId ?? generateSecureId(),
    userId: '',
    startedAt,
    endedAt,
    title: state.activeNoteTitle ?? `Meeting Notes from ${startedAt.toLocaleString()}`,
    summary: state.streamingSummary || state.savedNote?.summary || '',
    meeting: state.meeting ?? undefined,
    transcriptEntries,
    isActive: false,
  }
  const existingNotes = readBrowserAudioNotes().filter((note) => note.id !== savedNote.id)
  existingNotes.unshift(savedNote)
  persistBrowserAudioNotes(existingNotes)
  emitAudioNoteState({
    ...buildDefaultAudioNoteState(),
    status: 'saved',
    savedNote,
    savedNoteConfirmation: { noteId: savedNote.id, savedAt: Date.now() },
  })
  return undefined
}

export const handlePauseRecording = (): undefined => {
  const state = readBrowserAudioNoteState()
  if (state.status === 'recording') {
    emitAudioNoteState({ ...state, status: 'paused' })
  }
  return undefined
}

export const handleResumeRecording = (): undefined => {
  const state = readBrowserAudioNoteState()
  if (state.status === 'paused') {
    emitAudioNoteState({ ...state, status: 'recording' })
  }
  return undefined
}

export const handleClearAudioNoteState = (): undefined => {
  emitTranscriptEntries([])
  emitAudioNoteState(buildDefaultAudioNoteState())
  return undefined
}

export const handleClearDetectedMeeting = (): undefined => {
  const state = readBrowserAudioNoteState()
  emitAudioNoteState({ ...state, detectedMeeting: null })
  return undefined
}

export const handleGenerateSummaryForSavedNote = (): undefined => {
  const state = readBrowserAudioNoteState()
  const savedNote = state.savedNote
  if (!savedNote) {
    return undefined
  }
  const summarySource = savedNote.transcriptEntries?.length
    ? 'Summary generated from transcript in browser mode.'
    : 'Summary generated in browser mode.'
  const generatedSummary = savedNote.summary?.length ? savedNote.summary : summarySource
  const updatedNote: BrowserAudioNote = { ...savedNote, summary: generatedSummary }
  const notes = readBrowserAudioNotes().map((note) => (note.id === updatedNote.id ? updatedNote : note))
  persistBrowserAudioNotes(notes)
  emitAudioNoteState({
    ...state,
    summaryNoteId: savedNote.id,
    summaryPending: false,
    summaryError: null,
    streamingSummary: generatedSummary,
    savedNote: updatedNote,
  })
  return undefined
}
